import React, { useState, useEffect } from "react";
import useDataSourceManager from "hooks/useDataSourceManager";
import { v4 as uuidv4 } from "uuid";
import { ReactComponent as CheckedIcon } from "assets/images/icons/icn_checked.svg";
import { ReactComponent as UnCheckedIconIcon } from "assets/images/icons/icn_unchecked.svg";

import {
  Box,
  Typography,
  Divider,
  RadioGroup,
  Radio,
  FormControlLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

const ConfigRow = ({ showEditGeneral, label, value, onChange }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        mt: 2,
      }}
    >
      <Typography
        sx={{
          width: "200px",
          color: theme.palette.primary.greyPrimary,
          fontSize: theme.typography.customFontSize[2],
          fontWeight: theme.typography.customFontWeight[1],
        }}
      >
        {label}
      </Typography>

      <RadioGroup
        row
        value={value ? "enable" : "disable"}
        onChange={(e) => onChange(e.target.value === "enable")}
      >
        {["Enable", "Disable"].map((option) => (
          <FormControlLabel
            key={option}
            value={option.toLowerCase()}
            control={
              <Radio
                icon={<UnCheckedIconIcon />}
                checkedIcon={<CheckedIcon />}
              />
            }
            label={option}
            sx={{
              "& .MuiFormControlLabel-label": {
                fontSize: theme.typography.customFontSize[2],
                color: theme.palette.primary.greyPrimary,
              },
            }}
            disabled={!showEditGeneral}
          />
        ))}
      </RadioGroup>
    </Box>
  );
};

const WiredBasic = ({ showEditGeneral }) => {
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const theme = useTheme();

  const [wiredConfigUuid, setWiredConfigUuid] = useState(uuidv4());
  const [wiredConfigData, setWiredConfigData] = useState(null);
  const [updateUuid, setUpdateUuid] = useState(uuidv4());

  const breadcrumbs = JSON.parse(localStorage.getItem("selectBreadcrumbs"));
  const hasManageOrg = breadcrumbs.realm?.hasManageOrg === true;
  const realmId = breadcrumbs.realm?.id ?? null;
  const orgsId = breadcrumbs.orgs?.id ?? null;
  const siteId = breadcrumbs.site?.id ?? null;
  let entId = null;
  let orgId = null;

  if (hasManageOrg) {
    entId = realmId;
    if (orgsId) orgId = orgsId;
  } else {
    orgId = realmId;
  }

  useEffect(() => {
    getWiredConfig();
  }, []);

  const getWiredConfig = () => {
    const url = hasManageOrg
      ? `/ents/:entId/orgs/:orgId/sites/:siteId/settings/wiredConfig.read`
      : `/orgs/:orgId/sites/:siteId/settings/wiredConfig.read`;

    sendRequest(url, wiredConfigUuid, {
      entId,
      orgId,
      siteId,
    });
  };

  useEffect(() => {
    const wiredConfigResponse = getData(wiredConfigUuid);
    if (wiredConfigResponse?.data?.length) {
      setWiredConfigData(wiredConfigResponse.data[0]);
    }
  }, [dataMap]);

  const handleConfigChange = (key, newValue) => {
    const updatedData = {
      ...wiredConfigData,
      [key]: newValue,
    };
    setWiredConfigData(updatedData);

    const resourcePath =
      hasManageOrg && orgId
        ? "/ents/:entId/orgs/:orgId/sites/:siteId/settings/wiredConfig.update"
        : "/orgs/:orgId/sites/:siteId/settings/wiredConfig.update";

    const restfulParams =
      hasManageOrg && orgId ? { entId, orgId, siteId } : { orgId, siteId };

    const requestParams = {
      updates: {
        "wired.basic": {
          LBDState: updatedData.LBDState,
          DDPState: updatedData.DDPState,
        },
      },
    };

    sendRequest(resourcePath, updateUuid, restfulParams, requestParams);
  };

  if (!wiredConfigData) return null;

  return (
    <Box sx={{ padding: "20px 30px" }}>
      <Typography
        sx={{
          color: theme.palette.primary.main,
          fontSize: theme.typography.customFontSize[5],
          fontWeight: theme.typography.customFontWeight[1],
        }}
      >
        Global Settings
      </Typography>
      <Divider sx={{ margin: "20px 0" }} />

      <ConfigRow
        showEditGeneral={showEditGeneral}
        label="LBD Configuration"
        value={wiredConfigData.LBDState}
        onChange={(value) => handleConfigChange("LBDState", value)}
      />
      <ConfigRow
        showEditGeneral={showEditGeneral}
        label="DDP Configuration"
        value={wiredConfigData.DDPState}
        onChange={(value) => handleConfigChange("DDPState", value)}
      />
    </Box>
  );
};

export default WiredBasic;
